/**
 * Unified Event Tracking Utility
 * Handles client-side tracking for Meta Pixel, GA4, and Yandex Metrica
 * Plus server-side event mirroring via API
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event types
export const EVENT_TYPES = {
  PAGE_VIEW: 'PageView',
  VIEW_CONTENT: 'ViewContent',
  ADD_TO_CART: 'AddToCart',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  PURCHASE: 'Purchase',
  SEARCH: 'Search',
  LEAD: 'Lead',
  COMPLETE_REGISTRATION: 'CompleteRegistration'
};

// Generate unique event ID for deduplication
export const generateEventId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get stored tracking config
const getTrackingConfig = () => {
  try {
    const stored = localStorage.getItem('tracking_config');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Store tracking config
export const setTrackingConfig = (config) => {
  try {
    localStorage.setItem('tracking_config', JSON.stringify(config));
  } catch (e) {
    console.warn('Failed to store tracking config:', e);
  }
};

// Get Facebook browser cookie (fbp)
const getFbp = () => {
  try {
    const match = document.cookie.match(/_fbp=([^;]+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

// Get Facebook click ID (fbc)
const getFbc = () => {
  try {
    const match = document.cookie.match(/_fbc=([^;]+)/);
    if (match) return match[1];
    
    // Check URL for fbclid
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    if (fbclid) {
      return `fb.1.${Date.now()}.${fbclid}`;
    }
    return null;
  } catch {
    return null;
  }
};

// Get GA4 client ID
const getGaClientId = () => {
  try {
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.([^;]+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

/**
 * Initialize Meta Pixel
 */
export const initMetaPixel = (pixelId) => {
  if (!pixelId || typeof window === 'undefined') return;
  
  // Check if already initialized
  if (window.fbq) return;
  
  // Meta Pixel base code
  (function(f,b,e,v,n,t,s) {
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  
  console.log('[Tracking] Meta Pixel initialized:', pixelId);
};

/**
 * Initialize GA4
 */
export const initGA4 = (measurementId) => {
  if (!measurementId || typeof window === 'undefined') return;
  
  // Check if already initialized
  if (window.gtag) return;
  
  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: true
  });
  
  console.log('[Tracking] GA4 initialized:', measurementId);
};

/**
 * Initialize Yandex Metrica
 */
export const initYandexMetrica = (counterId) => {
  if (!counterId || typeof window === 'undefined') return;
  
  // Check if already initialized
  if (window.ym) return;
  
  // Yandex Metrica initialization code
  (function(m,e,t,r,i,k,a){
    m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
    m[i].l=1*new Date();
    for (var j = 0; j < document.scripts.length; j++) {
      if (document.scripts[j].src === r) { return; }
    }
    k=e.createElement(t);a=e.getElementsByTagName(t)[0];
    k.async=1;k.src=r;a.parentNode.insertBefore(k,a);
  })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
  
  window.ym(counterId, "init", {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
    ecommerce: "dataLayer"
  });
  
  // Initialize dataLayer for e-commerce if not exists
  window.dataLayer = window.dataLayer || [];
  
  console.log('[Tracking] Yandex Metrica initialized:', counterId);
};

/**
 * Track event to Yandex Metrica (browser-side)
 */
const trackYandexMetrica = (eventName, eventData, eventId) => {
  if (typeof window === 'undefined' || !window.ym) return false;
  
  // Get counter ID from localStorage config
  const config = getTrackingConfig();
  const counterId = config?.yandex?.counter_id;
  if (!counterId) return false;
  
  // Map event names to Yandex e-commerce actions
  const yandexEventMap = {
    'PageView': null, // Handled automatically
    'ViewContent': 'detail',
    'AddToCart': 'add',
    'InitiateCheckout': 'checkout',
    'Purchase': 'purchase',
    'Search': null,
    'Lead': null,
    'CompleteRegistration': null
  };
  
  const yandexAction = yandexEventMap[eventName];
  
  // For non-e-commerce events, use reachGoal
  if (!yandexAction) {
    window.ym(counterId, 'reachGoal', eventName.toLowerCase(), eventData);
    console.log('[Tracking] Yandex goal:', eventName);
    return true;
  }
  
  // Build e-commerce data
  const products = [];
  if (eventData.contents) {
    eventData.contents.forEach(item => {
      products.push({
        id: String(item.id || ''),
        name: item.name || '',
        price: item.price || 0,
        quantity: item.quantity || 1
      });
    });
  } else if (eventData.content_ids) {
    eventData.content_ids.forEach(id => {
      products.push({ id: String(id) });
    });
  }
  
  const ecommerceData = {
    ecommerce: {
      currencyCode: eventData.currency || 'USD'
    }
  };
  
  if (yandexAction === 'purchase') {
    ecommerceData.ecommerce.purchase = {
      actionField: {
        id: eventData.order_id || eventId,
        revenue: eventData.value || 0
      },
      products
    };
  } else if (yandexAction === 'add') {
    ecommerceData.ecommerce.add = { products };
  } else if (yandexAction === 'detail') {
    ecommerceData.ecommerce.detail = { products };
  } else if (yandexAction === 'checkout') {
    ecommerceData.ecommerce.checkout = { products };
  }
  
  // Push to dataLayer for Yandex e-commerce
  window.dataLayer.push(ecommerceData);
  
  console.log('[Tracking] Yandex e-commerce event:', yandexAction, eventId);
  return true;
};

/**
 * Track event to Meta Pixel (browser-side)
 */
const trackMetaPixel = (eventName, eventData, eventId) => {
  if (typeof window === 'undefined' || !window.fbq) return false;
  
  const metaEventMap = {
    'PageView': 'PageView',
    'ViewContent': 'ViewContent',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'Purchase': 'Purchase',
    'Search': 'Search',
    'Lead': 'Lead',
    'CompleteRegistration': 'CompleteRegistration'
  };
  
  const metaEventName = metaEventMap[eventName] || eventName;
  
  const metaData = {
    value: eventData.value || 0,
    currency: eventData.currency || 'USD',
    content_ids: eventData.content_ids || [],
    content_type: eventData.content_type || 'product'
  };
  
  if (eventData.contents) {
    metaData.contents = eventData.contents;
  }
  if (eventData.num_items) {
    metaData.num_items = eventData.num_items;
  }
  if (eventData.order_id) {
    metaData.order_id = eventData.order_id;
  }
  if (eventData.search_string) {
    metaData.search_string = eventData.search_string;
  }
  
  // Include eventID for deduplication with server events
  window.fbq('track', metaEventName, metaData, { eventID: eventId });
  
  console.log('[Tracking] Meta Pixel event:', metaEventName, eventId);
  return true;
};

/**
 * Track event to GA4 (browser-side)
 */
const trackGA4 = (eventName, eventData, eventId) => {
  if (typeof window === 'undefined' || !window.gtag) return false;
  
  const ga4EventMap = {
    'PageView': 'page_view',
    'ViewContent': 'view_item',
    'AddToCart': 'add_to_cart',
    'InitiateCheckout': 'begin_checkout',
    'Purchase': 'purchase',
    'Search': 'search',
    'Lead': 'generate_lead',
    'CompleteRegistration': 'sign_up'
  };
  
  const ga4EventName = ga4EventMap[eventName] || eventName.toLowerCase();
  
  const ga4Data = {
    currency: eventData.currency || 'USD',
    value: eventData.value || 0,
    event_id: eventId // Custom param for deduplication
  };
  
  // Add items for e-commerce events
  if (eventData.items) {
    ga4Data.items = eventData.items;
  } else if (eventData.contents) {
    ga4Data.items = eventData.contents.map(item => ({
      item_id: item.id,
      item_name: item.name || '',
      quantity: item.quantity || 1,
      price: item.price || 0
    }));
  }
  
  if (eventData.order_id) {
    ga4Data.transaction_id = eventData.order_id;
  }
  if (eventData.search_term) {
    ga4Data.search_term = eventData.search_term;
  }
  
  window.gtag('event', ga4EventName, ga4Data);
  
  console.log('[Tracking] GA4 event:', ga4EventName, eventId);
  return true;
};

/**
 * Send event to server for CAPI and Measurement Protocol
 */
const trackServer = async (eventName, eventData, region, userData, eventId) => {
  try {
    const response = await fetch(`${API_URL}/api/tracking/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        event_name: eventName,
        event_data: {
          ...eventData,
          source_url: window.location.href
        },
        region,
        user_data: {
          ...userData,
          client_user_agent: navigator.userAgent,
          fbp: getFbp(),
          fbc: getFbc(),
          ga_client_id: getGaClientId()
        },
        event_id: eventId,
        source: 'browser'
      })
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('[Tracking] Server event sent:', eventName, result);
      return result;
    }
  } catch (error) {
    console.warn('[Tracking] Server event failed:', error);
  }
  return null;
};

/**
 * Main tracking function - routes to all configured providers
 * @param {string} eventName - Event type (from EVENT_TYPES)
 * @param {object} eventData - Event data (value, currency, items, etc.)
 * @param {string} region - User's region (US, UK, TR)
 * @param {object} userData - Optional user data for server-side tracking
 */
export const trackEvent = async (eventName, eventData = {}, region = 'US', userData = null) => {
  const eventId = generateEventId();
  
  const results = {
    eventId,
    eventName,
    meta_pixel: false,
    ga4: false,
    yandex: false,
    server: null
  };
  
  // Track to Meta Pixel (browser)
  results.meta_pixel = trackMetaPixel(eventName, eventData, eventId);
  
  // Track to GA4 (browser)
  results.ga4 = trackGA4(eventName, eventData, eventId);
  
  // Track to Yandex Metrica (browser)
  results.yandex = trackYandexMetrica(eventName, eventData, eventId);
  
  // Track to server (for CAPI, MP, and Yandex Offline)
  // This is async and won't block the UI
  trackServer(eventName, eventData, region, userData, eventId)
    .then(serverResult => {
      results.server = serverResult;
    });
  
  return results;
};

/**
 * Track page view
 */
export const trackPageView = (pagePath, region = 'US') => {
  return trackEvent(EVENT_TYPES.PAGE_VIEW, {
    page_path: pagePath,
    page_title: document.title
  }, region);
};

/**
 * Track product view
 */
export const trackViewContent = (product, region = 'US') => {
  return trackEvent(EVENT_TYPES.VIEW_CONTENT, {
    content_ids: [product.id || product.slug],
    content_type: 'product',
    content_name: product.name,
    value: product.price || 0,
    currency: product.currency || 'USD'
  }, region);
};

/**
 * Track add to cart
 */
export const trackAddToCart = (product, quantity = 1, region = 'US') => {
  return trackEvent(EVENT_TYPES.ADD_TO_CART, {
    content_ids: [product.id || product.slug],
    content_type: 'product',
    contents: [{
      id: product.id || product.slug,
      name: product.name,
      quantity,
      price: product.price || 0
    }],
    value: (product.price || 0) * quantity,
    currency: product.currency || 'USD',
    num_items: quantity
  }, region);
};

/**
 * Track checkout initiation
 */
export const trackInitiateCheckout = (cart, region = 'US') => {
  const items = cart.items || [];
  const contents = items.map(item => ({
    id: item.product_id || item.id,
    name: item.name,
    quantity: item.quantity,
    price: item.price || 0
  }));
  
  return trackEvent(EVENT_TYPES.INITIATE_CHECKOUT, {
    content_ids: contents.map(c => c.id),
    content_type: 'product',
    contents,
    value: cart.total || 0,
    currency: cart.currency || 'USD',
    num_items: items.reduce((sum, item) => sum + item.quantity, 0)
  }, region);
};

/**
 * Track purchase
 */
export const trackPurchase = (order, region = 'US', userData = null) => {
  const items = order.items || [];
  const contents = items.map(item => ({
    id: item.product_id || item.id,
    name: item.name,
    quantity: item.quantity,
    price: item.price || 0
  }));
  
  return trackEvent(EVENT_TYPES.PURCHASE, {
    content_ids: contents.map(c => c.id),
    content_type: 'product',
    contents,
    order_id: order.order_id || order.id,
    value: order.total || 0,
    currency: order.currency || 'USD',
    num_items: items.reduce((sum, item) => sum + item.quantity, 0)
  }, region, userData);
};

/**
 * Track search
 */
export const trackSearch = (searchTerm, region = 'US') => {
  return trackEvent(EVENT_TYPES.SEARCH, {
    search_string: searchTerm,
    search_term: searchTerm
  }, region);
};

/**
 * Track lead/contact form submission
 */
export const trackLead = (formData = {}, region = 'US') => {
  return trackEvent(EVENT_TYPES.LEAD, formData, region);
};

/**
 * Track registration
 */
export const trackRegistration = (userData = {}, region = 'US') => {
  return trackEvent(EVENT_TYPES.COMPLETE_REGISTRATION, {
    content_name: 'registration'
  }, region, userData);
};

export default {
  EVENT_TYPES,
  generateEventId,
  initMetaPixel,
  initGA4,
  initYandexMetrica,
  trackEvent,
  trackPageView,
  trackViewContent,
  trackAddToCart,
  trackInitiateCheckout,
  trackPurchase,
  trackSearch,
  trackLead,
  trackRegistration,
  setTrackingConfig
};
